import React, { useContext, useState } from 'react';
import { Button, DatePicker, Form } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SALES_ORDERS_ENDPOINT } from '@totem/utilities/endpoints';

const FormItem = Form.Item;

const styles = {
  form: { width: '100%' },
  formItem: { paddingBottom: '0', marginBottom: '1rem' },
  datePicker: { width: '100%' },
  saveButton: { marginTop: '1rem' },
};

const ZERO_DATE = '0001-01-01T00:00:00Z';

const parseDate = (dateStr: string): Dayjs | null => {
  if (!dateStr || dateStr === ZERO_DATE) {
    return null;
  }
  // Dates are stored as UTC midnight; take the UTC calendar day so the
  // picker doesn't show the previous day in timezones west of UTC.
  const parsed = dayjs(dateStr.substring(0, 10));
  return parsed.isValid() ? parsed : null;
};

const toUTCMidnight = (date: Dayjs): string =>
  `${date.format('YYYY-MM-DD')}T00:00:00Z`;

export interface Props {
  salesOrderLine: SalesOrderLine;
  onSaved: () => void;
}

const SalesOrderLineDatesEditPanel = ({ salesOrderLine, onSaved }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(
    parseDate(salesOrderLine.startDate),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    parseDate(salesOrderLine.endDate),
  );

  const handleSave = () => {
    setIsSaving(true);

    fetch(`${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}/dates`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        startDate: startDate ? toUTCMidnight(startDate) : null,
        endDate: endDate ? toUTCMidnight(endDate) : null,
      }),
    }).then(() => {
      setIsSaving(false);

      onSaved();
    });
  };

  return (
    <Form layout="vertical" style={styles.form}>
      <FormItem label="Start Date" style={styles.formItem}>
        <DatePicker
          style={styles.datePicker}
          value={startDate}
          onChange={setStartDate}
          allowClear
        />
      </FormItem>
      <FormItem label="End Date" style={styles.formItem}>
        <DatePicker
          style={styles.datePicker}
          value={endDate}
          onChange={setEndDate}
          allowClear
        />
      </FormItem>
      <Button
        type="primary"
        onClick={handleSave}
        loading={isSaving}
        style={styles.saveButton}
      >
        Save Dates
      </Button>
    </Form>
  );
};

export default SalesOrderLineDatesEditPanel;
